import { supabase } from '../../commons/config/settings';
import axios from 'axios';

import { IOrderQuery } from './campaignsSlice';
import { IOrder } from './PaparazzoTypes';

// Type for line_items data from Supabase (snake_case column names)
interface SupabaseLineItem {
  id: number;
  order_id: number;
  line_item: string;
  mnz_line_ref: string | null;
  start_date: number;
  end_date: number;
  description: string | null;
  status: string;
  impressions: number | null;
}

// Funzione di test per verificare la connessione a Supabase
export async function testSupabaseConnection() {
  console.log('Verifica connessione a Supabase...');
  try {
    // Test di connessione base
    const { data, error } = await supabase
      .from('orders')
      .select('count()', { count: 'exact' });

    if (error) {
      console.error('Errore di connessione a Supabase:', error);
      return { success: false, error };
    }

    console.log('Connessione a Supabase riuscita. Conteggio records:', data);

    // Recupera i primi 5 record per verificare la struttura
    const { data: sampleData, error: sampleError } = await supabase
      .from('orders')
      .select('*')
      .limit(5);

    if (sampleError) {
      console.error('Errore nel recupero dei dati di esempio:', sampleError);
      return { success: true, count: data, error: sampleError };
    }

    console.log('Dati di esempio dalla tabella orders:', sampleData);
    return { success: true, count: data, sample: sampleData };
  } catch (err) {
    console.error('Errore imprevisto durante il test di connessione:', err);
    return { success: false, error: err };
  }
}

const buildQueryString = (query: IOrderQuery) => {
  const _q = '';
  const _limit = query.limit ? `&limit=${query.limit}` : '';
  const _status = query.status ? `&status=${query.status}` : '';
  const _device = query.device ? `&device=${query.device}` : '';
  const _from = query.from ? `&from=${query.from}` : '';
  const _to = query.to ? `&to=${query.to}` : '';
  const _mnzClientName = query.txtQuery ? `&txtQuery=${query.txtQuery}` : '';
  const _collection = '&collection=Orders13';

  return `${_q}${_limit}${_status}${_device}${_from}${_to}${_mnzClientName}${_collection}`;
};

/*
const fetchOrdersLastShooterWithMongo = async () => {
  const dataLastShooted = await axios.get(
    `${mongoEndpoint}/getLastShootedOrders?collection=${mongoSettingsCollection}`,
  );

  return dataLastShooted.data.value;
};

const fetchOrdersWithMongo = async (query: IOrderQuery) => {
  const qString = buildQueryString(query);
  const dataOrders = await axios.get(
    `${mongoEndpoint}/campaigns_v2?${qString}`,
  );
  const dataLastShooted = await axios.get(
    `${mongoEndpoint}/getLastShootedOrders?collection=${mongoSettingsCollection}`,
  );
  const orders = dataOrders.data;
  const ordersLastShooted = dataLastShooted.data.value;

  return [orders, ordersLastShooted];
};
*/
export const fetchOrdersWithSupabase = async (query: IOrderQuery) => {
  console.log('Esecuzione query su Supabase con parametri:', query);
  const qString = buildQueryString(query);
  console.log('Query string costruita:', qString);

  try {
    // Sostituisco la chiamata a mongo con una chiamata a supabase sulla tabella "orders"
    // che restituisce un array di oggetti come quello richiesto.
    // NB: Si assume che sia già disponibile un client supabase chiamato "supabase"
    // e che la tabella abbia i campi necessari.

    console.log('Inizio chiamata a Supabase...');
    // Prima recuperiamo solo gli ordini senza line_items per prestazioni migliori
    const ordersQuery = supabase
      .from('orders')
      .select(
        `
        order_name,
        id,
        mnz_order_num,
        mnz_client_name,
        desktop_events_count,
        mobile_events_count,
        tablet_events_count,
        end_date,
        start_date,
        status,
        devices,
        last_shot_time,
        placements,
        testate
      `,
      );
    
    // Ordino per last_shot_time in ordine decrescente (i più recenti prima)
    // e metto i valori NULL alla fine, ma solo se non ci sono altri filtri che potrebbero escluderli
    if (!query.status || query.status === '') {
      // Se non c'è filtro per status, ordiniamo normalmente
      ordersQuery.order('last_shot_time', { ascending: false, nullsFirst: false });
    } else {
      // Se c'è un filtro per status, ordiniamo per id come fallback
      ordersQuery.order('id', { ascending: false });
    }

    // Poi recuperiamo le line_items separatamente
    const lineItemsQuery = supabase
      .from('line_items')
      .select('id, order_id, line_item, mnz_line_ref, start_date, end_date, description, status, impressions');

    // Aggiungo filtro per order_name se query.txtQuery è valorizzato
    if (
      query.txtQuery &&
      query.txtQuery.trim() !== '' &&
      query.txtQuery !== '___notavalidquery__'
    ) {
      ordersQuery.ilike('order_name', `%${query.txtQuery}%`);
    }

    // Aggiungo filtro per devices se query.device è valorizzato
    // Cerca se il valore è presente nell'array devices
    if (query.device && query.device.trim() !== '') {
      ordersQuery.contains('devices', [query.device]);
    }

    // Aggiungo filtri per le date (from e to)
    // La logica è: mostra gli ordini se c'è una sovrapposizione tra l'intervallo del filtro
    // e l'intervallo dell'ordine (start_date - end_date)

    // Se è specificata una data di inizio filtro (query.from)
    if (query.from) {
      // Trova ordini che finiscono dopo la data di inizio filtro
      // (cioè l'end_date dell'ordine è >= alla data di inizio filtro)
      ordersQuery.gte('end_date', query.from);
    }

    // Se è specificata una data di fine filtro (query.to)
    if (query.to) {
      // Trova ordini che iniziano prima della data di fine filtro
      // (cioè lo start_date dell'ordine è <= alla data di fine filtro)
      ordersQuery.lte('start_date', query.to);
    }

    // Applico il limite agli ordini (ridotto per prestazioni)
    ordersQuery.limit(Math.min(query.limit || 50, 100)); // Max 100, default 50

    // Eseguiamo prima la query degli ordini per ottenere gli ID
    const ordersResult = await ordersQuery;
    const { data: ordersData, error: ordersError } = ordersResult;

    if (ordersError) {
      console.error('Errore nella query ordini:', ordersError);
      throw new Error('Errore nel recupero degli ordini da Supabase: ' + ordersError.message);
    }

    if (!ordersData || ordersData.length === 0) {
      console.warn('Nessun ordine restituito dalla query Supabase');
      return [[], []];
    }

    console.log('Ordini recuperati:', ordersData.length);

    // Ora recuperiamo solo le line_items per gli ordini recuperati
    const orderIds = ordersData.map(order => order.id);
    console.log('Recupero line_items per', orderIds.length, 'ordini');
    
    const lineItemsResult = await lineItemsQuery.in('order_id', orderIds);
    const { data: lineItemsData, error: lineItemsError } = lineItemsResult as { data: SupabaseLineItem[] | null; error: any };

    if (lineItemsError) {
      console.error('Errore nella query line_items:', lineItemsError);
      // Non blocchiamo se fallisce il recupero delle line_items, continuiamo con array vuoto
    }

    console.log('Risposta da Supabase:', {
      ordersCount: ordersData.length,
      lineItemsCount: lineItemsData?.length || 0,
      lineItemsError: lineItemsError?.message,
      sampleOrder: ordersData[0],
      sampleLineItem: lineItemsData?.[0],
    });

    // Creiamo una mappa delle line_items per order_id per accesso veloce
    const lineItemsMap = new Map<number, SupabaseLineItem[]>();
    if (lineItemsData) {
      lineItemsData.forEach((lineItem: SupabaseLineItem) => {
        if (!lineItemsMap.has(lineItem.order_id)) {
          lineItemsMap.set(lineItem.order_id, []);
        }
        lineItemsMap.get(lineItem.order_id)!.push(lineItem);
      });
    }

    const orders: IOrder[] = ordersData.map((order) => {
      const lineItemsArray = lineItemsMap.get(order.id) || [];

      return {
        id: order.id,
        order: order.order_name,
        mnzOrderNum: order.mnz_order_num,
        mnzClientName: order.mnz_client_name,
        deviceEvents: [],
        deviceEventsCount: {
          desktop: order.desktop_events_count || 0,
          mobile: order.mobile_events_count || 0,
          tablet: order.tablet_events_count || 0,
          total:
            (order.desktop_events_count || 0) +
            (order.mobile_events_count || 0) +
            (order.tablet_events_count || 0),
        },
        lineItemsCount: lineItemsArray.length,
        lineItems: lineItemsArray.map((lineItem: SupabaseLineItem) => ({
          id: lineItem.id,
          lineItem: lineItem.line_item,
          mnzLineRef: lineItem.mnz_line_ref || undefined,
          startDate: lineItem.start_date.toString(),
          endDate: lineItem.end_date.toString(),
          description: lineItem.description || '',
          status: lineItem.status,
          impressions: lineItem.impressions || 0,
        })),
        endDate: order.end_date,
        startDate: order.start_date,
        status: lineItemsArray.some(
          (lineItem: SupabaseLineItem) => lineItem.status === 'DELIVERING',
        )
          ? 'delivering'
          : 'completed',
        devices: order.devices,
        lastSavedEventTime: order.last_shot_time ? new Date(order.last_shot_time * 1000).toISOString() : '',
      };
    });

    console.log('Dati trasformati:', orders.length, 'ordini');
    console.log('Esempio ordine trasformato:', orders[0]);

    // Applico il filtro per status lato client se specificato
    let filteredOrders = orders;
    if (query.status && query.status.trim() !== '') {
      const statusFilter = query.status.toUpperCase();
      filteredOrders = orders.filter((order) =>
        order.lineItems.some((lineItem) => lineItem.status === statusFilter),
      );
      console.log(
        `Filtro status applicato. Ordini filtrati: ${filteredOrders.length} su ${orders.length}`,
      );
    }

    // @ts-ignore
    const ordersLastShooted = filteredOrders.map((order) => order.order);

    console.log('Risultato finale:', {
      ordersCount: filteredOrders.length,
      lastShootedCount: ordersLastShooted.length,
      firstOrder: filteredOrders[0],
    });

    return [filteredOrders, ordersLastShooted];
  } catch (err) {
    console.error('Errore imprevisto durante il recupero degli ordini:', err);
    throw err;
  }
};

export async function getCampaigns(query: IOrderQuery) {
  let orders, ordersLastShooted;

  [orders, ordersLastShooted] = await fetchOrdersWithSupabase(query);

  return {
    orders,
    ordersLastShooted,
  };
}

export async function getOrder(orderId: number) {
  console.log('Recupero ordine con ID:', orderId);

  try {
    // Recupero l'ordine con le sue line_items
    const { data: orderData, error: orderError } = await supabase
      .from('orders')
      .select(
        `
        order_name,
        id,
        mnz_order_num,
        mnz_client_name,
        desktop_events_count,
        mobile_events_count,
        tablet_events_count,
        line_items:line_items ( id, line_item, mnz_line_ref, start_date, end_date, description, status, impressions ),
        end_date,
        start_date,
        status,
        devices,
        last_shot_time,
        placements,
        testate
      `,
      )
      .eq('id', orderId)
      .single();

    if (orderError) {
      console.error("Errore nel recupero dell'ordine:", orderError);
      throw new Error(
        "Errore nel recupero dell'ordine da Supabase: " + orderError.message,
      );
    }

    if (!orderData) {
      throw new Error('Ordine non trovato');
    }

    // Recupero gli eventi dettagliati per questo ordine
    // Gli eventi sono collegati agli ordini tramite i line_items
    console.log('Recupero eventi tramite line_items per ordine ID:', orderId);

    let eventsData = [];

    if (orderData && orderData.line_items && orderData.line_items.length > 0) {
      // Estraggo gli ID dei line_items
      const lineItemIds = orderData.line_items.map((item) => item.id);
      console.log('Line item IDs trovati:', lineItemIds);

      // Recupero gli eventi che appartengono a questi line_items
      const { data: fullData, error: fullError } = await supabase
        .from('device_events')
        .select('*')
        .in('line_item_id', lineItemIds)
        .order('time', { ascending: false });

      if (fullError) {
        console.error('Errore nel recupero degli eventi:', fullError);
        eventsData = [];
      } else {
        eventsData = fullData || [];
        console.log(`Trovati ${eventsData.length} eventi per i line items`);
      }
    } else {
      console.log('Nessun line item trovato per questo ordine');
      eventsData = [];
    }

    console.log('Dati ordine recuperati:', orderData);
    console.log('Eventi recuperati:', eventsData.length);

    // Trasformo i dati nel formato richiesto
    const lineItemsArray = orderData.line_items
      ? Array.isArray(orderData.line_items)
        ? orderData.line_items
        : [orderData.line_items]
      : [];

    const result = {
      order: orderData.order_name,
      id: orderData.id,
      isTemp: false, // Assumo che gli ordini in Supabase non siano temporanei
      mnzOrderNum: orderData.mnz_order_num,
      mnzClientName: orderData.mnz_client_name,
      deviceEventsCount: {
        desktop: orderData.desktop_events_count || 0,
        mobile: orderData.mobile_events_count || 0,
        tablet: orderData.tablet_events_count || 0,
        total:
          (orderData.desktop_events_count || 0) +
          (orderData.mobile_events_count || 0) +
          (orderData.tablet_events_count || 0),
      },
      lineItemsCount: lineItemsArray.length,
      deviceEvents: eventsData.map((event) => ({
        ...event,
        // Normalize DB snake_case fields to app camelCase
        urlClippedBase: event.url_clipped_base,
        urlClippedUI: event.url_clipped_ui,
        urlFullPage: event.url_full_page,
        isIntro: event.is_intro,
        isSkin: event.is_skin,
        isMarked: event.is_marked,
        sourceURL: event.source_url,
        adUnitPath: event.ad_unit_path,
        slotElId: event.slot_el_id,
        sourcePriority: event.source_priority,
        dateTime: event.date_time,
        // Ensure lineItemId is available for filtering logic
        lineItemId: event.line_item_id,
      })),
      lineItems: lineItemsArray.map((lineItem) => ({
        id: lineItem.id,
        lineItem: lineItem.line_item,
        mnzLineRef: lineItem.mnz_line_ref,
        startDate: lineItem.start_date,
        endDate: lineItem.end_date,
        description: lineItem.description || '',
        status: lineItem.status,
        impressions: lineItem.impressions,
      })),
      endDate: orderData.end_date,
      startDate: orderData.start_date,
      status: lineItemsArray.some(
        (lineItem) => lineItem.status === 'DELIVERING',
      )
        ? 'delivering'
        : 'completed',
      devices: orderData.devices || [],
      lastSavedEventTime: orderData.last_shot_time ? new Date(orderData.last_shot_time * 1000).toISOString() : '',
      placements: orderData.placements || [],
      testate: orderData.testate || [],
    };
    console.log('Oggetto ordine trasformato:', result);
    return result;
  } catch (err) {
    console.error("Errore imprevisto durante il recupero dell'ordine:", err);
    throw err;
  }
}

export async function getUsers() {
  const data = await axios.get(
    'https://fra1.digitaloceanspaces.com/kf-service/paparazzo/data/paparazzo_test_users.json',
  );
  console.log(data);
  return data;
}

// Upload a new screenshot/device event to Supabase using minimal fields from the UI
export async function uploadScreenshotToSupabase(eventData: {
  orderId: number;
  type: string;
  device: string;
  placement: string;
  urlClippedUI: string;
  urlClippedBase: string;
  urlFullPage: string;
  isIntro: boolean;
  isSkin: boolean;
  testata: string;
  lineItem: string;
}) {
  try {
    // Find the line_item_id by line item name and order id
    const { data: lineItemRow, error: lineItemError } = await supabase
      .from('line_items')
      .select('id')
      .eq('line_item', eventData.lineItem)
      .eq('order_id', eventData.orderId)
      .single();

    if (lineItemError) {
      throw new Error(`Line item non trovato: ${lineItemError.message}`);
    }

    if (!lineItemRow?.id) {
      throw new Error('Line item non trovato');
    }

    const now = new Date();

    // Minimal insert payload matching DB columns, derived where necessary
    const insertPayload: any = {
      // Provide a client-side id to satisfy NOT NULL on id when no default exists
      id: now.getTime(),
      line_item_id: lineItemRow.id,
      type: eventData.type,
      device: eventData.device,
      placement: eventData.placement,
      testata: eventData.testata || null,
      url_clipped_ui: eventData.urlClippedUI,
      url_clipped_base: eventData.urlClippedBase,
      url_full_page: eventData.urlFullPage,
      is_intro: eventData.isIntro,
      is_skin: eventData.isSkin,
      // Ensure sorting works as elsewhere we order by time desc
      time: now.getTime(),
    };

    const { data, error } = await supabase
      .from('device_events')
      .insert([insertPayload])
      .select()
      .single();

    if (error) {
      throw new Error(`Errore nell'upload dello screenshot: ${error.message}`);
    }

    return data;
  } catch (err) {
    console.error('Errore durante uploadScreenshotToSupabase:', err);
    throw err;
  }
}
